<template>
  <div class="mx-auto">
    <!-- Loading Indicator -->
    <SkeletonCpdTokenTabLoader v-if="loading"/>

    <!-- Detailed Course Table -->
    <div v-else class="bg-white shadow-md overflow-hidden">
      <table class="w-full">
        <thead class="bg-gray-100 border-b">
        <tr>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Course Name</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Funding</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Maximum Coupons</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Used Coupons</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Remaining Coupons</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Start Date</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Deadline</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Coupon Code</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Status</th>
          <th class="py-2 px-3 text-left text-gray-600 font-semibold">Action</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="course in discountCourses"
            :key="course.course_id"
            class="border-b hover:bg-gray-50"
        >
          <td class="py-2 px-3">{{ course.name || 'Unknown Course' }}</td>
          <td class="py-2 px-3">{{ course.funding || 'N/A' }}</td>
          <td class="py-2 px-3">{{ course.total_coupons || 0 }}</td>
          <td class="py-2 px-3">{{ course.used_coupons || 0 }}</td>
          <td class="py-2 px-3">{{ course.remaining_learners || 0 }}</td>
          <td class="py-2 px-3">{{ formatDate(course.start_date) }}</td>
          <td class="py-2 px-3">{{ formatDate(course.deadline) }}</td>
          <td class="py-2 px-3">
            <div v-if="course.status === 'Active' && course.coupon_code" class="flex items-center space-x-2">
              <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">{{ course.coupon_code }}</span>
              <button
                @click="copyCouponCode(course.coupon_code)"
                class="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs transition-colors"
                title="Copy coupon code"
              >
                Copy
              </button>
            </div>
            <span v-else class="text-gray-400 text-sm">-</span>
          </td>
          <td class="py-2 px-3">
            <span :class="getStatusClass(course.status)" class="px-2 py-1 rounded-full text-xs font-medium">
              {{ course.status || 'Unknown' }}
            </span>
          </td>
          <td class="py-2 px-3">
            <button
              v-if="course.status === 'Active'"
              @click="viewCourseDetails(course)"
              class="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 transition-colors"
            >
              View Details
            </button>
            <button
              v-else-if="course.status === 'Inactive'"
              @click="openPaymentForCourse(course)"
              class="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
            >
              Pay
            </button>
            <button
              v-else
              @click="viewCourseDetails(course)"
              class="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
            >
              View Details
            </button>
          </td>
        </tr>
        <tr v-if="discountCourses.length === 0" class="text-center">
          <td colspan="10" class="py-4 px-3 text-gray-500">No discount courses available</td>
        </tr>
        </tbody>
      </table>

      <!-- Pagination for Detailed Table -->
      <div v-if="discountCourses.length > 0" class="flex justify-center items-center my-4">
        <div class="flex space-x-2">
          <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
              v-for="page in totalPages"
              :key="page"
              @click="changePage(page)"
              :class="{
                'bg-blue-900 text-white': currentPage === page,
                'text-gray-600': currentPage !== page,
              }"
              class="px-3 py-1 rounded hover:bg-blue-100"
          >
            {{ page }}
          </button>
          <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Create Course Coupon Modal -->
    <div v-if="showCouponModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-screen overflow-y-auto">
        <!-- Modal Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">Create New Coupon</h2>
          <button 
            @click="closeCouponModal" 
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <form @submit.prevent="submitCoupon" class="p-6 space-y-4">
          <!-- Course Search -->
          <div class="course-search-container">
            <label class="block text-sm font-medium text-gray-700 mb-2" for="course-search">
              Search a course <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                id="course-search"
                v-model="courseSearchQuery"
                type="text"
                placeholder="Search for a course..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :class="{ 'border-red-500': errors.courseId }"
                @input="filterCourses"
                @focus="showCourseDropdown = true"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              
              <!-- Dropdown Results -->
              <div 
                v-if="showCourseDropdown && filteredCourses.length > 0" 
                class="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
              >
                <div
                  v-for="course in filteredCourses"
                  :key="course.id"
                  @click="selectCourse(course)"
                  class="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                >
                  <div class="font-medium text-gray-900">{{ course.name }}</div>
                </div>
              </div>
              
              <!-- No Results -->
              <div 
                v-if="showCourseDropdown && courseSearchQuery && filteredCourses.length === 0" 
                class="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-center text-gray-500"
              >
                No courses found matching "{{ courseSearchQuery }}"
              </div>
            </div>
            
            <!-- Selected Course Display -->
            <div v-if="selectedCourseDisplay" class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="font-medium text-blue-900">{{ selectedCourseDisplay.name }}</div>
                  <div class="text-sm text-blue-700 mt-1">
                    Course Fee: KES {{ selectedCourseDisplay.course_fee?.toLocaleString() || '0' }}
                  </div>
                </div>
                <button
                  @click="clearSelectedCourse"
                  type="button"
                  class="text-blue-600 hover:text-blue-800 ml-3"
                  aria-label="Clear selection"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <p v-if="errors.courseId" class="mt-1 text-sm text-red-600">{{ errors.courseId }}</p>
          </div>

          <!-- Maximum Learners -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="max-learners">
              Maximum Number of Learners <span class="text-red-500">*</span>
            </label>
            <input
              id="max-learners"
              v-model="couponForm.maximumLearners"
              type="number"
              min="1"
              step="1"
              placeholder="Enter maximum number of learners"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': errors.maximumLearners }"
              required
            >
            <p v-if="errors.maximumLearners" class="mt-1 text-sm text-red-600">{{ errors.maximumLearners }}</p>
          </div>

          <!-- Discount Amount -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="discount-amount">
              Discount Amount (KES) <span class="text-red-500">*</span>
            </label>
            <input
              id="discount-amount"
              v-model="couponForm.discountAmount"
              type="number"
              :min="selectedCourseDisplay?.payable_fee || selectedCourseDisplay?.course_fee || 0"
              step="0.01"
              :placeholder="`Minimum: KES ${(selectedCourseDisplay?.payable_fee || selectedCourseDisplay?.course_fee)?.toLocaleString() || '0'}`"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-500': errors.discountAmount }"
              @input="validateDiscountAmount"
              required
            >
            <p v-if="errors.discountAmount" class="mt-1 text-sm text-red-600">{{ errors.discountAmount }}</p>
            <p class="mt-1 text-sm text-gray-500">
              <span v-if="selectedCourseDisplay">
                Minimum: KES {{ (selectedCourseDisplay.payable_fee || selectedCourseDisplay.course_fee)?.toLocaleString() || '0' }} 
                - No maximum limit
              </span>
              <span v-else>
                Select a course to set discount amount
              </span>
            </p>
          </div>

          <!-- Expiration Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2" for="expiration-datetime">
              Expiration Date & Time <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <Flatpickr
                id="expiration-datetime"
                v-model="couponForm.expirationDate"
                :config="couponDateTimeConfig"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :class="{ 'border-red-500': errors.expirationDate }"
                placeholder="Select expiration date and time"
                required
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-6 8h6m-9 4h12m-9-8h.01M4 4h16v16H4V4z"></path>
                </svg>
              </div>
            </div>
            <p v-if="errors.expirationDate" class="mt-1 text-sm text-red-600">{{ errors.expirationDate }}</p>
            <p class="mt-1 text-sm text-gray-500">
              Expiration time will be saved in UTC format with 'Z' timezone indicator
            </p>
          </div>

          <!-- Legal Disclaimer -->
          <div class="flex items-start space-x-3">
            <div class="flex items-center h-5">
              <input
                id="disclaimer-checkbox"
                v-model="couponForm.disclaimerAccepted"
                type="checkbox"
                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                :class="{ 'border-red-500': errors.disclaimerAccepted }"
                required
              >
            </div>
            <div class="text-sm">
              <label for="disclaimer-checkbox" class="text-gray-700">
                I have reviewed the <button type="button" class="text-blue-600 hover:text-blue-800 underline" @click="showDisclaimerPopup = true">legal disclaimer</button> that applies when discounting this course.
              </label>
            </div>
          </div>
          <p v-if="errors.disclaimerAccepted" class="text-sm text-red-600">{{ errors.disclaimerAccepted }}</p>

          <!-- Form Actions -->
          <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="closeCouponModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              :disabled="isSubmitting"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
              <span v-else>Create Coupon</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Course Funding Modal -->
    <div v-if="showCourseFundingModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="bg-white p-6 rounded-lg shadow-lg w-1/2 max-h-screen overflow-y-auto">
        <!-- Modal Header -->
        <div class="flex justify-between border-b pb-2">
          <h2 class="text-lg font-semibold">Create Course Funding</h2>
          <button @click="showCourseFundingModal = false" class="text-gray-600 hover:text-red-500">✕</button>
        </div>

        <div class="flex flex-col px-3 mt-4">
          <label class="block font-bold text-sm museo-sans-bold" for="category"><span class="text-red-500">*</span>Select Course</label>
          <div class="relative">
            <select id="category" v-model="selectedCourse" class="border-gray-300 rounded p-2 mt-1 bg-gray-100 w-full">
              <option value="" disabled selected>Select</option>
              <option v-for="course in courses" :key="course.id" :value="course.id">
                {{ course.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex flex-col px-3 mt-4">
          <label class="block font-bold text-sm">
            <span class="text-red-500">*</span>Discount Timeline
          </label>
          <div class="flex space-x-4 mt-1">
            <!-- From Date -->
            <div class="flex-1">
              <div class="relative">
                <Flatpickr
                  v-model="fromDate"
                  :config="flatpickrConfig"
                  class="border-gray-300 rounded p-2 bg-gray-100 w-full"
                  placeholder="From"
                />
                <span class="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg class="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-6 8h6m-9 4h12m-9-8h.01M4 4h16v16H4V4z"></path>
                  </svg>
                </span>
              </div>
            </div>
            <!-- To Date -->
            <div class="flex-1">
              <div class="relative">
                <Flatpickr
                  v-model="toDate"
                  :config="toFlatpickrConfig"
                  class="border-gray-300 rounded p-2 bg-gray-100 w-full"
                  placeholder="To"
                />
                <span class="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg class="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-6 8h6m-9 4h12m-9-8h.01M4 4h16v16H4V4z"></path>
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col px-3 mt-4">
          <label class="block font-bold text-sm"><span class="text-red-500">*</span>Number of Learners</label>
          <input
            v-model="numberOfLearners"
            type="text"
            placeholder="Enter Number of learners"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-100"/>
        </div>

        <div class="flex items-center mt-4 px-3 mb-4">
          <input id="default-checkbox" type="checkbox" v-model="disclaimerAccepted" 
            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 focus:ring-2"
            required>
          <label for="default-checkbox" class="text-lg font-bold text-black px-7">
            I have reviewed the <a class="text-blue-600 cursor-pointer" @click="showDisclaimerPopup = true">legal disclaimer</a> that applies when discounting this course.
          </label>
        </div>

        <div class="flex justify-end mt-4">
          <button type="button" @click="showCourseFundingModal = false" class="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400">
            <span v-if="submitting" class="flex items-center justify-center">
              <img :src="loaderUrl" alt="Logging In.." class="w-6 h-6"/>
            </span>
            <span v-else>Cancel</span>
          </button>
          <button type="button" @click="submitFunding"
                  class="ml-2 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-blue-600">
            <span v-if="submitting" class="flex items-center justify-center">
              <img :src="loaderUrl" alt="Logging In.." class="w-6 h-6"/>
            </span>
            <span v-else>Create Funding</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Modal -->
    <div v-if="showPaymentModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <!-- Modal Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">M-Pesa Payment</h2>
          <button 
            @click="closePaymentModal" 
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close payment modal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <!-- Coupon Information -->
          <div v-if="createdCoupon" class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 class="font-semibold text-blue-900 mb-2">Coupon Created</h3>
            <p class="text-sm text-blue-800">
              <strong>Coupon ID:</strong> {{ createdCoupon.coupon_code || 'N/A' }}
            </p>
            <p class="text-sm text-blue-800">
              <strong>Course:</strong> {{ createdCoupon.course?.name || createdCoupon.course_name || 'N/A' }}
            </p>
            <p class="text-sm text-blue-800">
              <strong>Discount Amount:</strong> KES {{ parseFloat(createdCoupon.discount_amount || 0).toLocaleString() }}
            </p>
            <p v-if="!createdCoupon.id" class="text-xs text-red-600 mt-2">
              Debug: Coupon data available but no ID found
            </p>
          </div>
          
          <!-- Debug info when coupon data is missing -->
          <div v-else class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 class="font-semibold text-red-900 mb-2">Error</h3>
            <p class="text-sm text-red-800">No coupon data found. Please try creating the coupon again.</p>
          </div>

          <!-- Payment Form -->
          <div v-if="!paymentInitiated" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2" for="phone-number">
                M-Pesa Phone Number
              </label>
              <div class="flex items-center">
                <div class="shrink-0 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg">
                  +254
                </div>
                <div class="relative w-full">
                  <input
                    id="phone-number"
                    v-model="phoneNumber"
                    type="text"
                    placeholder="707 205 896"
                    class="block p-2.5 w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                    required
                  >
                </div>
              </div>
              <p class="mt-1 text-sm text-gray-500">
                Enter your M-Pesa registered phone number
              </p>
            </div>

            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-yellow-800">Payment Information</h3>
                  <div class="mt-1 text-sm text-yellow-700">
                    <p>You will receive an M-Pesa prompt on your phone for payment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Confirmation -->
          <div v-else class="space-y-4">
            <div class="text-center py-4">
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Payment Request Sent</h3>
              <p class="text-sm text-gray-600 mb-4">
                Please check your phone for the M-Pesa prompt and complete the payment.
              </p>
              <button
                @click="confirmPayment"
                class="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            v-if="!paymentInitiated && createdCoupon?.id"
            type="button"
            @click="initiatePayment"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="paymentSubmitting || !phoneNumber.trim()"
          >
            <span v-if="paymentSubmitting" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
            <span v-else>Send Payment Request</span>
          </button>
          <button
            type="button"
            @click="closePaymentModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            {{ paymentInitiated ? 'Close' : 'Cancel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Legal Disclaimer Modal -->
    <div v-if="showDisclaimerPopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div class="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden">
        <!-- Modal Header -->
        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="bg-white bg-opacity-20 rounded-full p-2 mr-3">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h2 class="text-xl font-bold text-white">Legal Disclaimer</h2>
            </div>
            <button 
              type="button" 
              class="text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
              @click="closeDisclaimerPopup" 
              aria-label="Close disclaimer"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="px-6 py-6 overflow-y-auto max-h-[60vh]">
          <div class="space-y-4">
            <!-- Platform Info -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-center mb-2">
                <div class="bg-blue-600 rounded-full p-1 mr-2">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 21h10"></path>
                  </svg>
                </div>
                <h3 class="text-lg font-semibold text-blue-900">Jiji Health E-Learning Platform</h3>
              </div>
            </div>

            <!-- Main Content -->
            <div class="space-y-4">
              <p class="text-gray-700 leading-relaxed">
                <span class="font-semibold">Important Notice:</span> Sponsors who choose to prefund courses and generate discount tokens for learners are advised to exercise due diligence and caution in the handling, distribution, and storage of such tokens.
              </p>

              <!-- Liability Section -->
              <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <svg class="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                  </div>
                  <div class="ml-3">
                    <h4 class="text-sm font-semibold text-yellow-800 mb-2">Liability Disclaimer</h4>
                    <p class="text-sm text-yellow-700 font-medium">
                      Jiji Health shall not be held liable for any losses, unauthorized access, or misuse arising from:
                    </p>
                  </div>
                </div>
              </div>

              <!-- Risk List -->
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="font-semibold text-gray-900 mb-3">Potential Risks Include:</h4>
                <ul class="space-y-2">
                  <li class="flex items-start">
                    <div class="bg-red-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg class="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                    <span class="text-gray-700">Discount tokens being shared with unintended recipients</span>
                  </li>
                  <li class="flex items-start">
                    <div class="bg-red-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg class="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                    <span class="text-gray-700">Tokens being publicly distributed or leaked without proper controls</span>
                  </li>
                  <li class="flex items-start">
                    <div class="bg-red-100 rounded-full p-1 mr-3 mt-0.5">
                      <svg class="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                    <span class="text-gray-700">Any resulting unauthorized enrollments or access to sponsored courses</span>
                  </li>
                </ul>
              </div>

              <!-- Responsibility Section -->
              <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                <div class="flex items-start">
                  <div class="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                    <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="font-semibold text-green-900 mb-2">Sponsor Responsibility</h4>
                    <p class="text-sm text-green-800">
                      Once generated and issued, the <strong>responsibility for safeguarding and controlling the use of discount tokens lies solely with the sponsor.</strong> We recommend that tokens be shared only through secure, verifiable channels to the intended beneficiaries.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Agreement Section -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-start">
                  <div class="bg-blue-100 rounded-full p-1 mr-3 mt-0.5">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="font-semibold text-blue-900 mb-2">Agreement to Terms</h4>
                    <p class="text-sm text-blue-800">
                      By proceeding with the use of discount tokens, the sponsor acknowledges and agrees to these terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-600">Please review this disclaimer carefully</p>
            <div class="flex space-x-3">
              <button
                type="button"
                @click="closeDisclaimerPopup"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { CoursesAPI } from '../../services/courses';
import SkeletonCpdTokenTabLoader from '../Loaders/SkeletonCpdTokenTabLoader.vue';
import Flatpickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import loaderUrl from "@/assets/images/loader.svg";
import { useToast } from "vue-toastification";

export default defineComponent({
  name: 'AdminDiscountCourses',
  components: { SkeletonCpdTokenTabLoader, Flatpickr },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },
  expose: ['openCouponModal'],
  emits: ['update-statistics'],
   setup(props, { emit }) {
     const router = useRouter();
    const discountCourses = ref([]);
    const statistics = ref(null);
    const loading = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const perPage = ref(10);
    
    // Modal reactive properties
    const showCourseFundingModal = ref(false);
    const showCouponModal = ref(false);
    const showPaymentModal = ref(false);
    const showDisclaimerPopup = ref(false);
    const selectedCourse = ref('');
    const fromDate = ref(null);
    const toDate = ref(null);
    const numberOfLearners = ref('');
    const disclaimerAccepted = ref(false);
    const submitting = ref(false);
    const isSubmitting = ref(false);
    const paymentSubmitting = ref(false);
    const courses = ref([]);
    const toast = useToast();

    // Payment modal properties
    const createdCoupon = ref(null);
    const phoneNumber = ref('');
    const paymentInitiated = ref(false);

    // Coupon form data and validation
    const couponForm = ref({
      courseId: '',
      maximumLearners: '',
      discountAmount: '',
      expirationDate: '',
      disclaimerAccepted: false
    });

    const errors = ref({
      courseId: '',
      maximumLearners: '',
      discountAmount: '',
      expirationDate: '',
      disclaimerAccepted: ''
    });

    // Course search functionality
    const courseSearchQuery = ref('');
    const showCourseDropdown = ref(false);
    const filteredCourses = ref([]);
    const selectedCourseDisplay = ref(null);

    const formatNumber = (value) => {
      return typeof value === 'number' ? value.toLocaleString('en-US') : String(value);
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return 'Invalid Date';
      }
    };

    const getStatusClass = (status) => {
      switch (status?.toLowerCase()) {
        case 'active':
        case 'running':
          return 'bg-green-100 text-green-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        case 'completed':
        case 'finished':
          return 'bg-blue-100 text-blue-800';
        case 'cancelled':
        case 'expired':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const copyCouponCode = async (couponCode) => {
      try {
        await navigator.clipboard.writeText(couponCode);
        toast.success('Coupon code copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy coupon code:', error);
        toast.error('Failed to copy coupon code');
      }
    };

    const viewCourseDetails = async (course) => {
      console.log('Viewing course details:', course);
      
      // Navigate to the DiscountedUsersByCourse page with the course ID and name
      router.push({ 
        name: 'DiscountedUsersByCourse', 
        params: { 
          course_id: course.course_id,
          course_name: course.name
        }
      });
    };

    const createFunding = async () => {
      console.log('Creating new funding...');
      // Open the modal directly since it's now in this component
      showCourseFundingModal.value = true;
      // Fetch courses when modal opens
      fetchCourses();
    };

    // Flatpickr configurations
    const flatpickrConfig = {
      mode: 'range',
      dateFormat: 'Y-m-d',
      minDate: 'today',
      enableTime: false,
    };

    const toFlatpickrConfig = {
      mode: 'single',
      dateFormat: 'Y-m-d',
      minDate: 'today',
      enableTime: false,
      onChange: (selectedDates) => {
        if (selectedDates[0] && fromDate.value && selectedDates[0] < fromDate.value) {
          toDate.value = fromDate.value;
        } else {
          toDate.value = selectedDates[0] || null;
        }
      },
    };

    // Coupon modal date-time configuration
    const couponDateTimeConfig = {
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y-m-d H:i',
      minDate: 'today',
      altInput: true,
      altFormat: 'F j, Y at h:i K',
      onChange: (selectedDates) => {
        if (selectedDates[0]) {
          // Validate that the selected date is in the future
          const now = new Date();
          if (selectedDates[0] <= now) {
            errors.value.expirationDate = 'Expiration date must be in the future';
          } else {
            errors.value.expirationDate = '';
          }
        }
      },
    };

    const fetchCourses = async () => {
      try {
        console.log('Fetching courses...');
        
        // Import and set API system
        const { setApiSystem } = await import('@/services/api');
        setApiSystem('eLearning');
        console.log('API system set to eLearning');
        
        const apiResponse = await CoursesAPI.getActiveCourses();
        console.log('=== API Response Details ===');
        console.log('Full response:', apiResponse);
        console.log('Response type:', typeof apiResponse);
        console.log('Response keys:', Object.keys(apiResponse));
        
        // Extract the actual data from axios response
        const response = apiResponse.data;
        console.log('Response data:', response);
        console.log('Has courses property:', 'courses' in response);
        console.log('courses is array:', Array.isArray(response?.courses));
        console.log('response is array:', Array.isArray(response));
        
        // Handle the response structure from getActiveCourses
        if (response.courses && Array.isArray(response.courses)) {
          console.log('Using response.courses format');
          courses.value = response.courses.map((course) => ({
            id: course.course_id,
            name: course.name,
            course_fee: course.course_fee,
            payable_fee: course.payable_fee || course.course_fee // Fallback to course_fee if payable_fee not available
          }));
          console.log('Mapped courses:', courses.value);
        } else if (Array.isArray(response)) {
          console.log('Using direct array format');
          courses.value = response.map((course) => ({
            id: course.course_id,
            name: course.name,
            course_fee: course.course_fee,
            payable_fee: course.payable_fee || course.course_fee // Fallback to course_fee if payable_fee not available
          }));
          console.log('Mapped courses from array:', courses.value);
        } else {
          console.error('❌ Unexpected response format:', response);
          console.error('Response structure details:', {
            hasCourses: !!response?.courses,
            coursesIsArray: Array.isArray(response?.courses),
            responseIsArray: Array.isArray(response),
            responseKeys: Object.keys(response || {})
          });
          courses.value = [];
          toast.error('Unexpected response format when loading courses');
        }
        
        console.log('Final courses array:', courses.value);
      } catch (error) {
        console.error("❌ Error fetching courses:", error);
        courses.value = [];
        
        // Show more specific error information
        if (error.response) {
          console.error('HTTP Error:', error.response.status, error.response.data);
          toast.error(`API Error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          console.error('Network Error: No response received');
          toast.error('Network Error: Unable to reach the server');
        } else {
          console.error('Request Error:', error.message);
          toast.error('Request Error: ' + error.message);
        }
      }
    };

    const submitFunding = async () => {
      try {
        submitting.value = true;
        
        // Validate form
        if (!selectedCourse.value || !fromDate.value || !toDate.value || !numberOfLearners.value || !disclaimerAccepted.value) {
          toast.error('Please fill in all required fields');
          return;
        }

        // Mock submission - replace with actual API call
        console.log('Submitting funding with data:', {
          courseId: selectedCourse.value,
          fromDate: fromDate.value,
          toDate: toDate.value,
          numberOfLearners: numberOfLearners.value,
          disclaimerAccepted: disclaimerAccepted.value
        });

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        toast.success('Funding created successfully!');
        showCourseFundingModal.value = false;
        
        // Reset form
        selectedCourse.value = '';
        fromDate.value = null;
        toDate.value = null;
        numberOfLearners.value = '';
        disclaimerAccepted.value = false;
        
      } catch (error) {
        console.error('Error creating funding:', error);
        toast.error('Error creating funding');
      } finally {
        submitting.value = false;
      }
    };

    // Discount amount validation
    const validateDiscountAmount = () => {
      if (!selectedCourseDisplay.value) {
        errors.value.discountAmount = 'Please select a course first';
        return;
      }

      const discountAmount = parseFloat(couponForm.value.discountAmount);
      const payableFee = selectedCourseDisplay.value.payable_fee || selectedCourseDisplay.value.course_fee;

      if (!couponForm.value.discountAmount) {
        errors.value.discountAmount = 'Discount amount is required';
        return;
      }

      if (isNaN(discountAmount) || discountAmount <= 0) {
        errors.value.discountAmount = 'Discount amount must be a positive number';
        return;
      }

      if (payableFee && discountAmount < payableFee) {
        errors.value.discountAmount = `Discount amount must be at least KES ${payableFee.toLocaleString()}`;
        return;
      }

      // Clear any previous errors
      errors.value.discountAmount = '';
    };

    // Coupon validation and submission methods
    const validateCouponForm = () => {
      // Clear previous errors
      Object.keys(errors.value).forEach(key => {
        errors.value[key] = '';
      });

      let isValid = true;

      // Validate course selection
      if (!couponForm.value.courseId) {
        errors.value.courseId = 'Please select a course';
        isValid = false;
      }

      // Validate discount amount
      const discountAmount = parseFloat(couponForm.value.discountAmount);
      if (!couponForm.value.discountAmount) {
        errors.value.discountAmount = 'Discount amount is required';
        isValid = false;
      } else if (isNaN(discountAmount) || discountAmount <= 0) {
        errors.value.discountAmount = 'Discount amount must be a positive number';
        isValid = false;
      } else if (selectedCourseDisplay.value) {
        const payableFee = selectedCourseDisplay.value.payable_fee || selectedCourseDisplay.value.course_fee;
        if (discountAmount < payableFee) {
          errors.value.discountAmount = `Discount amount must be at least KES ${payableFee.toLocaleString()}`;
          isValid = false;
        }
      }

      // Validate maximum learners
      const maxLearners = parseInt(couponForm.value.maximumLearners);
      if (!couponForm.value.maximumLearners) {
        errors.value.maximumLearners = 'Maximum number of learners is required';
        isValid = false;
      } else if (isNaN(maxLearners) || maxLearners <= 0) {
        errors.value.maximumLearners = 'Maximum learners must be a positive integer';
        isValid = false;
      } else if (!Number.isInteger(maxLearners)) {
        errors.value.maximumLearners = 'Maximum learners must be a whole number';
        isValid = false;
      }

      // Validate expiration date
      if (!couponForm.value.expirationDate) {
        errors.value.expirationDate = 'Expiration date and time is required';
        isValid = false;
      } else {
        const expirationDate = new Date(couponForm.value.expirationDate);
        const now = new Date();
        if (expirationDate <= now) {
          errors.value.expirationDate = 'Expiration date must be in the future';
          isValid = false;
        }
      }

      // Validate disclaimer acceptance
      if (!couponForm.value.disclaimerAccepted) {
        errors.value.disclaimerAccepted = 'You must accept the legal disclaimer to proceed';
        isValid = false;
      }

      return isValid;
    };

    const formatExpirationDateToISO = (dateString) => {
      const date = new Date(dateString);
      // Format to ISO 8601 with UTC timezone (ending with Z)
      return date.toISOString();
    };

    const submitCoupon = async () => {
      if (!validateCouponForm()) {
        toast.error('Please fix the validation errors before submitting');
        return;
      }

      try {
        isSubmitting.value = true;

        // Format expiration date to ISO 8601 with UTC timezone
        const isoExpirationDate = formatExpirationDateToISO(couponForm.value.expirationDate);

        // Get the selected course to get its fee
        const selectedCourse = courses.value.find(course => course.id === couponForm.value.courseId);
        if (!selectedCourse) {
          toast.error('Selected course not found');
          return;
        }

        // Prepare the payload wrapped in course_coupon key
        const payload = {
          course_coupon: {
            course_id: couponForm.value.courseId,
            maximum_learners: parseInt(couponForm.value.maximumLearners),
            discount_amount: parseFloat(couponForm.value.discountAmount),
            expires_at: isoExpirationDate
          }
        };

        console.log('Submitting coupon with payload:', payload);

        // Use the existing API method
        const apiResponse = await CoursesAPI.createCourseCoupons(couponForm.value.courseId, payload);

        console.log('Coupon creation response:', apiResponse);

        // Extract the actual data from axios response
        const couponResponse = apiResponse.data;

        // Handle different response structures
        let couponData;
        if (couponResponse.coupon) {
          // If wrapped in coupon key
          couponData = couponResponse.coupon;
        } else if (couponResponse.course_coupon) {
          // If wrapped in course_coupon key
          couponData = couponResponse.course_coupon;
        } else {
          // Direct coupon object
          couponData = couponResponse;
        }

        // Store the created coupon for payment
        createdCoupon.value = couponData;
        
        console.log('Stored coupon data:', createdCoupon.value);
        console.log('Coupon ID:', createdCoupon.value?.id);

        toast.success('Coupon created successfully!');
        
        // Close coupon modal and show payment modal
        closeCouponModal();
        showPaymentModal.value = true;
        
        // Refresh the list
        loadDiscountCourses();
        
      } catch (error) {
        console.error('Error creating coupon:', error);
        
        // Handle specific validation errors from server
        if (error.message) {
          const serverErrors = JSON.parse(error.message);
          if (serverErrors.errors) {
            Object.keys(serverErrors.errors).forEach(field => {
              const errorKey = field;
              if (errorKey in errors.value) {
                errors.value[errorKey] = serverErrors.errors[field];
              }
            });
          }
          toast.error(error.message || 'Failed to create coupon');
        } else {
          toast.error('Failed to create coupon. Please check your connection and try again.');
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const openCouponModal = async () => {
      showCouponModal.value = true;
      await fetchCourses(); // Ensure courses are loaded
      
      console.log('Courses loaded:', courses.value.length);
      console.log('Sample course:', courses.value[0]);
      
      // Focus the search input when modal opens
      setTimeout(() => {
        const searchInput = document.querySelector('#course-search');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    };

    const closeCouponModal = () => {
      showCouponModal.value = false;
      
      // Reset form and errors
      couponForm.value = {
        courseId: '',
        maximumLearners: '',
        expirationDate: '',
        disclaimerAccepted: false
      };
      
      Object.keys(errors.value).forEach(key => {
        errors.value[key] = '';
      });
    };

    // Payment methods
    const initiatePayment = async () => {
      if (!phoneNumber.value.trim()) {
        toast.error('Please enter a phone number');
        return;
      }

      if (phoneNumber.value.length < 9) {
        toast.error('Please enter a valid phone number');
        return;
      }

      if (!createdCoupon.value?.id) {
        toast.error('Invalid coupon data. Please try creating the coupon again.');
        return;
      }

      try {
        paymentSubmitting.value = true;
        
        const formattedPhone = `254${phoneNumber.value.trim()}`;
        const couponId = createdCoupon.value.id;
        
        console.log('Initiating payment for coupon ID:', couponId);
        console.log('Phone number:', formattedPhone);
        
        await CoursesAPI.initiateCouponPayment(couponId, {
          phone_number: formattedPhone
        });
        
        paymentInitiated.value = true;
        toast.success('Payment request sent successfully!');
        
      } catch (error) {
        console.error('Error initiating payment:', error);
        toast.error(error.message || 'Failed to initiate payment');
      } finally {
        paymentSubmitting.value = false;
      }
    };

    const confirmPayment = async () => {
      try {
        // Make a direct API call to get the specific coupon
        const apiResponse = await CoursesAPI.getCourseCoupons({ 
          course_id: createdCoupon.value.course_id 
        });
        
        // Extract the actual data from axios response
        const couponResponse = apiResponse.data;
        
        const targetCoupon = couponResponse.course_coupons.find(
          (coupon) => coupon.id === createdCoupon.value.id
        );
        
        if (targetCoupon?.payment_status) {
          toast.success('Payment confirmed successfully!');
          closePaymentModal();
          // Refresh the coupons list to show updated status
          await loadDiscountCourses();
        } else {
          toast.error('Payment not confirmed. Please complete the M-Pesa payment and try again.');
        }
        
      } catch (error) {
        console.error('Error confirming payment:', error);
        toast.error('Failed to confirm payment status. Please try again.');
      }
    };

    const closePaymentModal = () => {
      showPaymentModal.value = false;
      paymentInitiated.value = false;
      phoneNumber.value = '';
      createdCoupon.value = null;
    };

    const openPaymentForCourse = async (course) => {
      try {
        // Find the actual coupon data for this course
        const apiResponse = await CoursesAPI.getCourseCoupons({ 
          course_id: course.course_id 
        });
        
        // Extract the actual data from axios response
        const response = apiResponse.data;
        
        // Find the specific coupon with Inactive status
        const inactiveCoupon = response.course_coupons.find(
          (coupon) => coupon.course_id === course.course_id && coupon.coupon_status === 'Inactive'
        );
        
        if (inactiveCoupon) {
          createdCoupon.value = inactiveCoupon;
          showPaymentModal.value = true;
          paymentInitiated.value = false; // Reset payment state
          console.log('Opening payment for inactive coupon:', inactiveCoupon);
        } else {
          toast.error('No inactive coupon found for this course');
        }
      } catch (error) {
        console.error('Error fetching coupon for payment:', error);
        toast.error('Failed to load coupon data for payment');
      }
    };

    // Handle escape key to close modal
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showCouponModal.value) {
        closeCouponModal();
      }
    };

    // Watch for modal state changes to manage event listener
    watch(showCouponModal, (isOpen) => {
      if (isOpen) {
        document.addEventListener('keydown', handleEscapeKey);
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('keydown', handleEscapeKey);
        document.removeEventListener('click', handleClickOutside);
      }
    });

    // Handle clicking outside the dropdown to close it
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.course-search-container')) {
        showCourseDropdown.value = false;
      }
    };

    // Cleanup event listener on unmount
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleClickOutside);
    });

    // Course search methods
    const filterCourses = () => {
      console.log('Filtering courses...');
      console.log('Search query:', courseSearchQuery.value);
      console.log('Available courses:', courses.value.length);
      
      if (!courseSearchQuery.value.trim()) {
        filteredCourses.value = [...courses.value]; // Create a new array reference
        console.log('No query, showing all courses:', filteredCourses.value.length);
      } else {
        const query = courseSearchQuery.value.toLowerCase().trim();
        filteredCourses.value = courses.value.filter(course => 
          course.name?.toLowerCase().includes(query)
        );
        console.log('Filtered results:', filteredCourses.value.length);
      }
    };

    const selectCourse = (course) => {
      couponForm.value.courseId = course.id;
      selectedCourseDisplay.value = course;
      courseSearchQuery.value = course.name;
      showCourseDropdown.value = false;
      errors.value.courseId = ''; // Clear any previous errors
      
      // Set discount amount to payable fee by default
      const payableFee = course.payable_fee || course.course_fee;
      if (payableFee) {
        couponForm.value.discountAmount = payableFee.toString();
      } else {
        couponForm.value.discountAmount = '';
      }
      
      // Validate discount amount for the new course
      validateDiscountAmount();
    };

    const clearSelectedCourse = () => {
      couponForm.value.courseId = '';
      selectedCourseDisplay.value = null;
      courseSearchQuery.value = '';
      showCourseDropdown.value = false;
      filteredCourses.value = courses.value;
      // Clear discount amount when course is cleared
      couponForm.value.discountAmount = '';
      errors.value.discountAmount = '';
    };

    // Initialize filtered courses when courses are loaded
    watch(courses, (newCourses) => {
      console.log('Courses changed, updating filtered courses:', newCourses.length);
      filteredCourses.value = [...newCourses]; // Create a new array reference
      if (!couponForm.value.courseId) {
        clearSelectedCourse();
      }
    }, { immediate: true });

    const closeDisclaimerPopup = () => {
      showDisclaimerPopup.value = false;
    };

    const loadDiscountCourses = async (page = 1) => {
      try {
        loading.value = true;

        const params = {
          page,
          per_page: perPage.value,
          search: props.searchQuery || undefined,
        };

        console.log('Loading discount courses with params:', params);
        
        // Call the actual API to get course coupons
        const apiResponse = await CoursesAPI.getCourseCoupons(params);
        const { course_coupons, total_stats, meta } = apiResponse.data;

        // Transform API data to match component interface
        const transformedCourses = course_coupons.map((coupon) => ({
          course_id: coupon.course_id,
          name: coupon.course?.name || 'Unknown Course',
          funding: `KES ${formatNumber(coupon.discount_amount)}`,
          total_coupons: coupon.maximum_learners,
          used_coupons: coupon.learner_number_used,
          remaining_learners: coupon.maximum_learners - coupon.learner_number_used,
          start_date: coupon.created_at,
          deadline: coupon.expires_at,
          status: coupon.coupon_status,
          coupon_code: coupon.coupon_code
        }));

        discountCourses.value = transformedCourses;
        
        // Use the real statistics from API response
        const totalUsedCoupons = transformedCourses.reduce((sum, course) => {
          return sum + course.used_coupons;
        }, 0);
        
        const totalRemainingCoupons = transformedCourses.reduce((sum, course) => {
          return sum + course.remaining_learners;
        }, 0);
        
        const activeDiscounts = total_stats.total_active_coupons || transformedCourses.filter((course) => course.status === 'Active').length;
        
        statistics.value = {
          active_discounts: activeDiscounts,
          total_used_coupons: totalUsedCoupons,
          total_remaining_learners: totalRemainingCoupons
        };

        // Set pagination from real API response
        totalPages.value = meta.total_pages;
        currentPage.value = meta.current_page;
        perPage.value = meta.per_page;

        // Emit statistics for parent component
        emit('update-statistics', statistics.value);

      } catch (error) {
        console.error('Failed to load discount courses:', error.message, error.stack);
        discountCourses.value = [];
        statistics.value = null;
      } finally {
        loading.value = false;
      }
    };

    const changePage = (page) => {
      if (
        page >= 1 &&
        page <= totalPages.value &&
        page !== currentPage.value
      ) {
        currentPage.value = page;
        loadDiscountCourses(currentPage.value);
      }
    };

    // Watch for searchQuery changes
    watch(
      () => props.searchQuery,
      (newSearchQuery) => {
        console.log('searchQuery changed:', newSearchQuery);
        currentPage.value = 1; // Reset to first page on search
        loadDiscountCourses(1);
      }
    );

    // Load data when component mounts
    onMounted(() => {
      console.log('AdminDiscountCourses mounted');
      loadDiscountCourses();
    });

    return {
      discountCourses,
      statistics,
      loading,
      currentPage,
      totalPages,
      perPage,
      formatNumber,
      formatDate,
      getStatusClass,
      viewCourseDetails,
      changePage,
      createFunding,
      openCouponModal,
      // Modal properties
      showCourseFundingModal,
      showCouponModal,
      showPaymentModal,
      showDisclaimerPopup,
      selectedCourse,
      fromDate,
      toDate,
      numberOfLearners,
      disclaimerAccepted,
      submitting,
      isSubmitting,
      paymentSubmitting,
      courses,
      couponForm,
      errors,
      courseSearchQuery,
      showCourseDropdown,
      filteredCourses,
      selectedCourseDisplay,
      createdCoupon,
      phoneNumber,
      paymentInitiated,
      loaderUrl,
      // Configurations
      flatpickrConfig,
      toFlatpickrConfig,
      couponDateTimeConfig,
      // Methods
      fetchCourses,
      submitFunding,
      submitCoupon,
      closeCouponModal,
      closePaymentModal,
      closeDisclaimerPopup,
      validateCouponForm,
      validateDiscountAmount,
      formatExpirationDateToISO,
      filterCourses,
      selectCourse,
      clearSelectedCourse,
      initiatePayment,
      confirmPayment,
      openPaymentForCourse,
      copyCouponCode,
      openCouponModal
    };
  },
});
</script>